/**
 * Lift2Fit — prefetch: fill the read cache before the first visit.
 *
 * A prefetch is only a `loadOnce` + `write` into the Instant Paint cache, which
 * is why it inherits every rule for free: seeds never overwrite fresher data,
 * mutations invalidate, and the TTL bounds how long a filled entry lives.
 *
 * Deliberately small. Today it prefetches the one key that several coach screens
 * share — the roster — because that is the page a coach opens first and every
 * other coach screen reads the same entry. A prefetch into a key nothing reads
 * is wasted traffic; add a screen here only once that screen paints from cache.
 */
import { api } from './api';
import { calendarKey, weekRange } from './calendarKeys';
import { instantPaintEnabled, loadOnce, peek, write } from './readCache';

/** `VITE_PREFETCH=1` enables it; anything else leaves the app exactly as it is. */
export function prefetchEnabled(): boolean {
  return import.meta.env?.VITE_PREFETCH === '1' && instantPaintEnabled();
}

/**
 * Warm the roster for a coach/gym account, after the first paint.
 *
 * `requestIdleCallback` keeps it off the critical path; the timeout fallback
 * covers Safari. A failure is swallowed on purpose — this is an optimisation,
 * never a dependency.
 */
export function prefetchCoachRoster(): void {
  if (!prefetchEnabled()) return;
  const run = () => {
    if (peek('coaching/clients') !== null) return; // already warm
    void loadOnce('coaching/clients', () => api.coachClients())
      .then((result) => {
        if (result.data !== null && result.data !== undefined) write('coaching/clients', result.data);
      })
      .catch(() => {
        /* a prefetch that fails is a prefetch that never happened */
      });
  };
  // This week's calendar, for the same reason: it is the second screen a coach
  // opens, and its key embeds a date range the shell would otherwise have to
  // guess. `weekRange()` is the screen's own function, so the keys cannot drift.
  const runCalendar = () => {
    const range = weekRange();
    const key = calendarKey(range);
    if (peek(key) !== null) return;
    void loadOnce(key, () => api.crmSlots(range.from, range.to))
      .then((result) => {
        if (result.data !== null && result.data !== undefined) write(key, result.data);
      })
      .catch(() => {
        /* silent, as above */
      });
  };
  const idle = (globalThis as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void })
    .requestIdleCallback;
  const both = () => {
    run();
    // Sequential, not parallel: the calendar waits for the roster so the two
    // prefetches never compete with each other for the first paint's bandwidth.
    setTimeout(runCalendar, 400);
  };
  if (typeof idle === 'function') idle(both, { timeout: 2000 });
  else setTimeout(both, 1200);
}
